import { BillingPeriod, Policy, PolicyBilling, PolicyBillingSettings, PolicyLayer } from "@deathstar/types/northstar";
import { DateTime } from "@deathstar/types/util";
import { QueryKey, useMutation, UseMutationOptions, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { plainToClass } from "class-transformer";
import { PolicyWithBillingInfo } from "../../features/policies/policies-list/PoliciesList";
import { blazar } from "../util/blazar";
import { ResponseError } from "../util/exceptions";

const fullPolicyMap = new Map<
    string,
    {
        policy: Policy;
        cacheBust: number;
    }
>();
const layerMetadataMap = new Map<
    string,
    {
        metadata: PolicyLayer.Metadata[];
        cacheBust: number;
    }
>();

const CACHED_DURATION = 20 * 60 * 1000; // twenty minutes in milliseconds

export interface FindLayerMetadataQueryOptions {
    policy: Pick<Policy, "id" | "createdDate" | "amsClientNumber" | "accountId">;
    includeLatestLayersOnly?: boolean;
}

export const policies = {
    queryKeys: {
        find: (accountId: string) => [accountId, "driver-of-the-year"] as QueryKey,
    },

    cacheBust: (policyId: string) => {
        fullPolicyMap.delete(policyId);
        layerMetadataMap.delete(policyId);
    },

    find: (accountId: string, filter: "active" | "inactive") => {
        return blazar.fetchJson<PolicyWithBillingInfo[]>(`/waypoint/orgs/${accountId}/policies/${filter}`);
    },

    useFind: <T = PolicyWithBillingInfo[]>(
        accountId: string,
        filter: "active" | "inactive",
        config?: Partial<UseQueryOptions<PolicyWithBillingInfo[], ResponseError, T>>
    ) =>
        useQuery<PolicyWithBillingInfo[], ResponseError, T>({
            queryKey: ["policies", accountId!, filter],
            queryFn: () => policies.find(accountId, filter),
            ...config,
            enabled: !!accountId!,
            retry: (count, error) => {
                return count < 3 && error.status !== 403;
            },
        }),

    findBillingPeriods: async (accountId: string, policyId: string) => {
        const billingPeriods = await blazar.fetchJson<BillingPeriod[]>(`/waypoint/orgs/${accountId}/policies/${policyId}/billing-periods`);
        return billingPeriods.map((bp) => {
            return {
                ...bp,
                startDate: new DateTime(bp.startDate),
                endDate: new DateTime(bp.endDate),
                dueDate: new DateTime(bp.dueDate),
            };
        }) as BillingPeriod[];
    },

    useFindBillingPeriods: <T = BillingPeriod[]>(
        accountId: string,
        policyId: string,
        config?: Partial<UseQueryOptions<BillingPeriod[], ResponseError, T>>
    ) =>
        useQuery<BillingPeriod[], ResponseError, T>({
            queryKey: ["policies", "billing-periods", accountId!, policyId],
            queryFn: () => policies.findBillingPeriods(accountId, policyId),
            ...config,
            enabled: !!accountId!,
            retry: (count, error) => {
                return count < 3 && error.status !== 403;
            },
        }),

    findBillingSettings: (accountId: string, policyId: string) => {
        return blazar.fetchJson<PolicyBillingSettings>(`/waypoint/orgs/${accountId}/policies/${policyId}/billing-settings`);
    },

    useFindBillingSettings: <T = PolicyBillingSettings>(
        accountId: string,
        policyId: string,
        config?: Partial<UseQueryOptions<PolicyBillingSettings, ResponseError, T>>
    ) =>
        useQuery<PolicyBillingSettings, ResponseError, T>({
            queryKey: ["policies", "billing-settings", accountId, policyId],
            queryFn: () => policies.findBillingSettings(accountId, policyId),
            ...config,
            enabled: !!accountId!,
            retry: (count, error) => {
                return count < 3 && error.status !== 403 && error.status !== 404;
            },
        }),

    findFullPolicy: async (accountId: string, policyId: string) => {
        if (fullPolicyMap.has(policyId)) {
            const result = fullPolicyMap.get(policyId);
            if (result && result.cacheBust > DateTime.now()) {
                return result.policy;
            }
            fullPolicyMap.delete(policyId);
        }
        const policyDto = await blazar.fetchJson<Policy>(`/waypoint/orgs/${accountId}/policies/${policyId}/get-full-policy`);
        const policy = plainToClass(Policy, policyDto);
        PolicyBilling.reGenerateFromJson(policy, []);
        fullPolicyMap.set(policyId, {
            policy,
            cacheBust: DateTime.now() + CACHED_DURATION,
        });
        return policy;
    },

    updateBillingSettings: async (accountId: string, policyId: string, data: Partial<PolicyBillingSettings>) => {
        const response = await blazar.fetchJson<PolicyBillingSettings>(
            `/waypoint/orgs/${accountId}/policies/${policyId}/billing-settings`,
            {
                method: "PATCH",
                body: JSON.stringify(data),
            }
        );
        const cachedPolicyRecord = fullPolicyMap.get(response.policyId);
        if (cachedPolicyRecord) {
            cachedPolicyRecord.policy.billingSettings = plainToClass(PolicyBillingSettings, response);
        }
        return plainToClass(PolicyBillingSettings, response);
    },

    useFindFullPolicy: <T = Policy>(accountId: string, policyId: string, config?: Partial<UseQueryOptions<Policy, ResponseError, T>>) =>
        useQuery<Policy, ResponseError, T>({
            queryKey: ["policies", "full", accountId!, policyId],
            queryFn: () => policies.findFullPolicy(accountId, policyId),
            ...config,
            enabled: !!accountId!,
            retry: (count, error) => {
                return count < 3 && error.status !== 403;
            },
        }),

    useFindFullPolicyMutation() {
        return useMutation<Policy, Error, { accountId: string; policyId: string }>({
            mutationFn: ({ accountId, policyId }) => policies.findFullPolicy(accountId, policyId),
        });
    },

    async findLayerMetadata(options: FindLayerMetadataQueryOptions): Promise<PolicyLayer.Metadata[]> {
        const policyId = options.policy.id;
        if (layerMetadataMap.has(policyId)) {
            const result = layerMetadataMap.get(policyId);
            if (result && result.cacheBust > DateTime.now()) {
                return result.metadata;
            }
            layerMetadataMap.delete(policyId);
        }
        const searchParams = new URLSearchParams();
        searchParams.set("includeLatestLayersOnly", String(options.includeLatestLayersOnly ?? false));
        const results = await blazar.fetchJson<PolicyLayer.Metadata[]>(
            `/waypoint/orgs/${options.policy.accountId}/policies/${policyId}/layers/metadata?${searchParams.toString()}`
        );
        const metadata = results?.map((l) => plainToClass(PolicyLayer.Metadata, l));
        layerMetadataMap.set(policyId, {
            metadata,
            cacheBust: DateTime.now() + CACHED_DURATION,
        });
        return metadata;
    },

    useFindLayerMetadata(
        options: FindLayerMetadataQueryOptions,
        config?: Partial<UseQueryOptions<PolicyLayer.Metadata[]>>
    ): UseQueryResult<PolicyLayer.Metadata[], Error> {
        return useQuery({
            queryKey: [options.policy.id, "findLayerMetadata", options.includeLatestLayersOnly],
            queryFn: () => policies.findLayerMetadata(options),
            ...config,
        });
    },

    useFindLayerMetadataMutation(config?: Partial<UseMutationOptions<PolicyLayer.Metadata[], Error, FindLayerMetadataQueryOptions>>) {
        return useMutation<PolicyLayer.Metadata[], Error, FindLayerMetadataQueryOptions>({
            ...config,
            mutationFn: (options) => policies.findLayerMetadata(options),
        });
    },
};
